import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import BackButton from "@/components/BackButton";
import CycleProgress from "@/components/CycleProgress";
import CheckoutModal from "@/components/CheckoutModal";
import {
  Ticket,
  Minus,
  Plus,
  Calendar,
  Sparkles,
  Coins,
  Lock,
  AlertCircle,
  Clock,
} from "lucide-react";

const STATUS_STYLES = {
  pending: { color: "#ff7a18", label: "Na čekanju" },
  approved: { color: "#22ff77", label: "Odobreno" },
  rejected: { color: "#ef4444", label: "Odbijeno" },
};

export default function BuyTickets() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [info, setInfo] = useState({ price_eur: 25, tokens_per_ticket: 25 });
  const [qty, setQty] = useState(1);
  const [purchases, setPurchases] = useState([]);
  const [pendingCrypto, setPendingCrypto] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  const loadAll = async () => {
    try {
      const [i, h, p] = await Promise.all([
        api.get("/tickets/info"),
        api.get("/tickets/purchases"),
        api.get("/payments/crypto/my-purchases"),
      ]);
      setInfo(i.data);
      setPurchases(h.data.purchases);
      setPendingCrypto(p.data.purchases);
    } catch {}
  };

  useEffect(() => {
    loadAll();
    if (params.get("canceled")) toast.error("Plaćanje je otkazano");
  }, [params]);

  const totalEur = qty * info.price_eur;
  const totalTokens = qty * info.tokens_per_ticket;

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="buy-tickets-page">
      <div className="flex justify-end mb-4"><BackButton /></div>
      <div>
        <p className="text-xs uppercase tracking-[0.4em] text-zinc-500">Kupovina tiketa</p>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-wide mt-1">
          Kupi <span className="text-neon-orange">NAŠA</span> tikete
        </h1>
        <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
          1 tiket = <span className="text-[#22ff77] font-bold">{info.price_eur}€ vrednosti</span> = <span className="text-[#22ff77] font-bold">{info.tokens_per_ticket} NAŠA Tokena</span>.
          Naplata: <span className="text-[#22ff77] font-bold">fiksno 28 USDT</span> po tiketu (TRC20).
        </p>
      </div>

      <CycleProgress />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 nasa-card p-6 md:p-8 relative overflow-hidden">
          <div className="absolute inset-0 nasa-grid-bg opacity-40" />
          <div className="relative">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-zinc-500 mb-4">
              <Coins className="w-3 h-3 text-[#22ff77]" /> Korpa
            </div>

            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500">Količina</div>
                <div className="font-display text-xs text-zinc-400 mt-1">
                  1 tiket = {info.price_eur}€ = {info.tokens_per_ticket} tokena
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  data-testid="qty-decrease"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="w-10 h-10 border border-white/10 rounded-sm hover:border-[#22ff77] text-[#22ff77] flex items-center justify-center"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <input
                  data-testid="qty-input"
                  type="number"
                  min={1}
                  value={qty}
                  onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-20 bg-black border border-white/10 px-3 py-2 text-center font-display text-2xl text-[#22ff77] rounded-sm focus:outline-none focus:border-[#22ff77]"
                />
                <button
                  data-testid="qty-increase"
                  onClick={() => setQty((q) => q + 1)}
                  className="w-10 h-10 border border-white/10 rounded-sm hover:border-[#22ff77] text-[#22ff77] flex items-center justify-center"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="border border-white/10 bg-black/40 p-4 rounded-sm">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Ukupno</div>
                <div className="font-display text-4xl font-bold text-[#ff7a18] mt-1" data-testid="total-eur">
                  {totalEur}€
                </div>
              </div>
              <div className="border border-white/10 bg-black/40 p-4 rounded-sm">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">NAŠA Tokeni</div>
                <div className="font-display text-4xl font-bold text-[#22ff77] mt-1 flex items-center gap-2" data-testid="total-tokens">
                  <Ticket className="w-6 h-6" />
                  {totalTokens}
                </div>
              </div>
            </div>

            <button
              onClick={() => setModalOpen(true)}
              data-testid="open-checkout-button"
              className="w-full flex items-center justify-center gap-3 bg-[#22ff77] text-black font-bold py-4 uppercase tracking-[0.2em] text-sm rounded-sm hover:brightness-110 transition-all glow-green"
            >
              <Coins className="w-4 h-4" />
              Kupi tiket · {totalEur}€ ({totalTokens} tokena)
            </button>

            <div className="mt-5 flex items-start gap-2 text-[11px] text-zinc-500">
              <Lock className="w-3 h-3 text-[#22ff77] mt-0.5 shrink-0" />
              <span>
                Plaćanje karticom ili direktnim USDT transferom. Status kupovine prelazi u "Na čekanju" dok administrator
                manuelno ne potvrdi uplatu na blockchain-u — tek tada se globalni brojač pomera i dobijate tokene.
              </span>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="nasa-card p-6 relative overflow-hidden">
            <div className="absolute inset-0 circuit-bg opacity-20" />
            <div className="absolute inset-0 bg-black/70" />
            <div className="relative">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-zinc-500">
                <Sparkles className="w-3 h-3 text-[#22ff77]" /> Trenutno tokena
              </div>
              <div className="font-display text-6xl font-bold text-neon-green mt-2 animate-pulse-neon" data-testid="current-balance">
                {user?.tickets ?? 0}
              </div>
              <p className="text-xs text-zinc-500 mt-2">Vaš ukupan saldo</p>
            </div>
          </div>

          <div className="border border-[#ff7a18]/30 bg-[#ff7a18]/5 p-4 rounded-sm">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-[#ff7a18] mt-0.5 shrink-0" />
              <p className="text-xs text-zinc-300 leading-relaxed">
                Kada se za jednu mašinu sakupi <span className="text-[#22ff77] font-bold">5.000 €</span>, ciklus se automatski zatvara i sistem izvlači pobednika. Odmah nakon toga počinje novi ciklus.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Pending crypto purchases */}
      {pendingCrypto.length > 0 && (
        <div className="nasa-card overflow-hidden" data-testid="pending-crypto-list">
          <div className="p-6 border-b border-white/5 flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#ff7a18]" />
            <h2 className="font-display text-lg">Moje USDT uplate — na čekanju</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 border-b border-white/5">
                  <th className="text-left px-6 py-3">Datum</th>
                  <th className="text-left px-6 py-3">Metod</th>
                  <th className="text-right px-6 py-3">Tiketi</th>
                  <th className="text-right px-6 py-3">EUR</th>
                  <th className="text-right px-6 py-3">USDT</th>
                  <th className="text-left px-6 py-3">TxID</th>
                  <th className="text-left px-6 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {pendingCrypto.map((p) => {
                  const st = STATUS_STYLES[p.status] || { color: "#888", label: p.status };
                  return (
                    <tr key={p.id} className="border-b border-white/5">
                      <td className="px-6 py-4 text-zinc-400 text-xs font-mono">
                        {new Date(p.created_at).toLocaleString("sr-Latn-RS")}
                      </td>
                      <td className="px-6 py-4 text-xs uppercase tracking-widest text-zinc-300">
                        {p.payment_method === "card" ? "Kartica" : "Kripto"}
                      </td>
                      <td className="px-6 py-4 text-right">{p.quantity}</td>
                      <td className="px-6 py-4 text-right font-mono text-[#ff7a18]">{p.amount_eur.toFixed(2)} €</td>
                      <td className="px-6 py-4 text-right font-mono text-[#22ff77]">{p.amount_usdt}</td>
                      <td className="px-6 py-4 font-mono text-[10px] text-zinc-500 max-w-[180px] truncate" title={p.tx_id}>
                        {p.tx_id}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm border"
                          style={{ color: st.color, borderColor: `${st.color}66`, background: `${st.color}10` }}
                        >
                          {st.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* History */}
      <div className="nasa-card overflow-hidden" data-testid="purchase-history">
        <div className="p-6 border-b border-white/5 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-[#ff7a18]" />
          <h2 className="font-display text-lg">Istorija odobrenih kupovina</h2>
        </div>
        {purchases.length === 0 ? (
          <div className="p-10 text-center text-zinc-500 text-sm">Još niste kupili nijedan tiket.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 border-b border-white/5">
                  <th className="text-left px-6 py-3">Datum</th>
                  <th className="text-right px-6 py-3">Količina</th>
                  <th className="text-right px-6 py-3">Plaćeno</th>
                  <th className="text-right px-6 py-3">Tokeni</th>
                  <th className="text-left px-6 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {purchases.map((p) => (
                  <tr key={p.id} className="border-b border-white/5">
                    <td className="px-6 py-4 text-zinc-400 text-xs font-mono">
                      {new Date(p.created_at).toLocaleString("sr-Latn-RS")}
                    </td>
                    <td className="px-6 py-4 text-right">{p.quantity}</td>
                    <td className="px-6 py-4 text-right font-mono text-[#ff7a18]">{p.amount_eur.toFixed(2)} €</td>
                    <td className="px-6 py-4 text-right font-display font-bold text-[#22ff77]">+{p.tokens_awarded}</td>
                    <td className="px-6 py-4">
                      <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm border text-[#22ff77] border-[#22ff77]/40 bg-[#22ff77]/5">
                        {p.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <CheckoutModal
        open={modalOpen}
        quantity={qty}
        onClose={() => {
          setModalOpen(false);
          loadAll();
        }}
        onSubmitted={() => loadAll()}
      />
    </div>
  );
}
